import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
  isRetrying: boolean;
}

/**
 * Error boundary that handles module loading errors and cache issues
 * Provides graceful fallback UI and recovery options
 */
export class ErrorBoundary extends Component<Props, State> {
  private retryTimeout?: NodeJS.Timeout;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, isRetrying: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Check if it's a module loading error
    const isModuleError = error.message.includes('Loading') || 
                          error.message.includes('fetch') ||
                          error.message.includes('chunk');
    
    return { 
      hasError: true, 
      error, 
      isRetrying: false 
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Call optional error handler
    this.props.onError?.(error, errorInfo);
    
    // Check if it's a cache-related error
    const isCacheError = error.message.includes('Loading chunk') ||
                        error.message.includes('Failed to fetch') ||
                        error.message.includes('MIME type');
    
    if (isCacheError) {
      console.warn('Cache-related error detected, suggesting page reload');
    }
  }

  handleRetry = () => {
    this.setState({ isRetrying: true });
    
    // Clear any stale cache data
    if ('caches' in window) {
      caches.keys().then(cacheNames => {
        cacheNames.forEach(cacheName => {
          if (cacheName.includes('fritidskompassen')) {
            caches.delete(cacheName);
          }
        });
      });
    }
    
    // Try to reset the error state after a short delay
    this.retryTimeout = setTimeout(() => {
      this.setState({ hasError: false, error: undefined, isRetrying: false });
    }, 1000);
  };

  handleReload = () => {
    window.location.reload();
  };

  componentWillUnmount() {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }
  }

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const isModuleError = this.state.error?.message.includes('Loading') ||
                           this.state.error?.message.includes('fetch') ||
                           this.state.error?.message.includes('chunk') ||
                           this.state.error?.message.includes('MIME');

      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="max-w-md w-full text-center space-y-6">
            <div className="flex justify-center">
              <AlertTriangle className="h-16 w-16 text-destructive" />
            </div>
            
            <div className="space-y-2">
              <h1 className="text-xl font-semibold text-foreground">
                {isModuleError ? 'Laddningsproblem' : 'Något gick fel'}
              </h1>
              <p className="text-muted-foreground">
                {isModuleError 
                  ? 'Det verkar som att webbsidans filer behöver uppdateras. Detta händer ibland efter längre inaktivitet.'
                  : 'Ett oväntat fel inträffade. Försök att ladda om sidan.'}
              </p>
            </div>

            {this.state.error && process.env.NODE_ENV === 'development' && (
              <details className="text-left bg-muted p-3 rounded text-sm">
                <summary className="cursor-pointer text-muted-foreground">
                  Teknisk information
                </summary>
                <pre className="mt-2 whitespace-pre-wrap text-xs">
                  {this.state.error.message}
                </pre>
              </details>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button 
                onClick={this.handleRetry}
                disabled={this.state.isRetrying}
                variant="outline"
                className="flex items-center gap-2"
              >
                {this.state.isRetrying ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                Försök igen
              </Button>
              
              <Button onClick={this.handleReload}>
                Ladda om sidan
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}